import { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import Card from 'components/card';
import { CONFIG } from 'variables/config';
import {
    MdSensors,
    MdChevronLeft,
    MdChevronRight,
    MdClose,
    MdSettings,
    MdEdit,
    MdDelete,
    MdCheckCircle,
    MdOutlineCheckCircle,
    MdRefresh,
    MdArchive,
    MdUnarchive,
    MdExpandMore,
} from 'react-icons/md';

type PixelStatus = 'green' | 'orange' | 'red';

interface PixelEntry {
    _id: string;
    url: string;
    last30DaysVisitors: number;
    industry?: string;
    subIndustry?: string;
    industryLastAttemptAt?: string;
    industryManual: boolean;
    archived: boolean;
    lastSeenAt: string;
    status: PixelStatus;
    sellerEmail?: string;
    iframeTemplateId?: string;
    iframeTemplateName?: string;
}

interface IndustryOption {
    name: string;
    subIndustries?: { name: string }[];
}

interface PixelGroup {
    hostname: string;
    rootUrl: string;
    pageCount: number;
    totalVisitors: number;
    lastSeenAt: string;
    status: PixelStatus;
    industry?: string;
    subIndustry?: string;
    industryMixed: boolean;
    sellerEmail?: string;
    iframeTemplateName?: string;
    iframeMixed: boolean;
    pages: PixelEntry[];
}

const STATUS_DOT_CLASS: Record<PixelStatus, string> = {
    green: 'bg-green-500',
    orange: 'bg-orange-500',
    red: 'bg-red-500',
};

const STATUS_LABEL: Record<PixelStatus, string> = {
    green: 'Seen in the last 24h',
    orange: 'Seen in the last 7 days',
    red: 'Not seen in over 7 days',
};

const STATUS_FILTER_OPTIONS: { value: PixelStatus; label: string }[] = [
    { value: 'green', label: 'Actif (24h)' },
    { value: 'orange', label: 'Ralenti (7j)' },
    { value: 'red', label: 'Inactif (+7j)' },
];

const DEBOUNCE_MS = 350;

const relativeTimeFormatter = new Intl.RelativeTimeFormat('fr', { numeric: 'auto' });
const RELATIVE_TIME_DIVISIONS: [Intl.RelativeTimeFormatUnit, number][] = [
    ['year', 60 * 60 * 24 * 365],
    ['month', 60 * 60 * 24 * 30],
    ['week', 60 * 60 * 24 * 7],
    ['day', 60 * 60 * 24],
    ['hour', 60 * 60],
    ['minute', 60],
    ['second', 1],
];

function formatRelative(value?: string): string {
    if (!value) return '—';
    const diffSeconds = Math.round((new Date(value).getTime() - Date.now()) / 1000);
    for (const [unit, secondsInUnit] of RELATIVE_TIME_DIVISIONS) {
        if (Math.abs(diffSeconds) >= secondsInUnit || unit === 'second') {
            return relativeTimeFormatter.format(Math.round(diffSeconds / secondsInUnit), unit);
        }
    }
    return '—';
}

function formatDate(value?: string): string {
    return value ? new Date(value).toLocaleString('fr-FR') : '—';
}

type PixelMode = 'mandatory' | 'optional' | 'hidden';

const PIXEL_MODE_OPTIONS: { value: PixelMode; label: string; desc: string }[] = [
    { value: 'mandatory', label: 'Obligatoire', desc: "Le vendeur doit confirmer l'avoir installé avant de continuer." },
    { value: 'optional', label: 'Optionnel', desc: "L'étape est affichée mais peut être passée." },
    { value: 'hidden', label: 'Caché', desc: "L'étape d'installation du pixel n'est pas affichée." },
];

const MAX_PRODUCT_SUGGESTIONS = 8;

// Small self-contained combobox: a text input with a capped, scrollable list of matching
// suggestions underneath — unlike a native <datalist>, which can't be styled/limited and
// renders inconsistently across browsers (Safari in particular).
function ProductSearchInput({
    value,
    onChange,
    options,
}: {
    value: string;
    onChange: (value: string) => void;
    options: string[];
}) {
    const [isOpen, setIsOpen] = useState(false);
    const blurTimeout = useRef<ReturnType<typeof setTimeout>>();

    const suggestions = useMemo(() => {
        const query = value.trim().toLowerCase();
        const matches = query ? options.filter((name) => name.toLowerCase().includes(query)) : options;
        return matches.slice(0, MAX_PRODUCT_SUGGESTIONS);
    }, [value, options]);

    return (
        <div className="relative">
            <input
                type="text"
                value={value}
                onChange={(e) => onChange(e.target.value)}
                onFocus={() => setIsOpen(true)}
                onBlur={() => {
                    blurTimeout.current = setTimeout(() => setIsOpen(false), 150);
                }}
                placeholder="ex: Pompe à chaleur..."
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none dark:border-navy-600 dark:bg-navy-700 dark:text-white dark:placeholder-gray-400"
            />
            {isOpen && suggestions.length > 0 && (
                <ul className="absolute z-10 mt-1 max-h-56 w-full overflow-y-auto rounded-lg border border-gray-200 bg-white py-1 shadow-lg dark:border-navy-600 dark:bg-navy-800">
                    {suggestions.map((name) => (
                        <li key={name}>
                            <button
                                type="button"
                                onMouseDown={(e) => {
                                    e.preventDefault(); // keep focus so onBlur doesn't fire before onClick
                                    clearTimeout(blurTimeout.current);
                                    onChange(name);
                                    setIsOpen(false);
                                }}
                                className="w-full truncate px-3 py-1.5 text-left text-sm hover:bg-gray-100 dark:hover:bg-navy-700 dark:text-white"
                            >
                                {name}
                            </button>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}

function EditPixelModal({
    entry,
    industries,
    onClose,
    onSave,
}: {
    entry: PixelEntry;
    industries: IndustryOption[];
    onClose: () => void;
    onSave: (industry: string, subIndustry: string) => Promise<void>;
}) {
    const [industry, setIndustry] = useState(entry.industry || '');
    const [subIndustry, setSubIndustry] = useState(entry.subIndustry || '');
    const [saving, setSaving] = useState(false);

    const subIndustries = useMemo(
        () => industries.find((i) => i.name === industry)?.subIndustries || [],
        [industries, industry]
    );

    const handleSave = async () => {
        if (!industry) return;
        setSaving(true);
        try {
            await onSave(industry, subIndustry);
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
            <div className="w-full max-w-md rounded-xl bg-white p-5 shadow-xl dark:bg-navy-800">
                <div className="mb-4 flex items-center justify-between">
                    <h3 className="font-semibold text-navy-700 dark:text-white">Modifier l'industrie</h3>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
                        <MdClose size={20} />
                    </button>
                </div>
                <p className="mb-4 truncate text-xs text-gray-500 dark:text-gray-400" title={entry.url}>{entry.url}</p>

                <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">Industrie</label>
                <select
                    value={industry}
                    onChange={(e) => { setIndustry(e.target.value); setSubIndustry(''); }}
                    className="mb-3 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                >
                    <option value="">Sélectionner...</option>
                    {industries.map((ind) => (
                        <option key={ind.name} value={ind.name}>{ind.name}</option>
                    ))}
                </select>

                <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">Produit</label>
                <select
                    value={subIndustry}
                    onChange={(e) => setSubIndustry(e.target.value)}
                    disabled={!industry}
                    className="mb-5 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none disabled:opacity-50 dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                >
                    <option value="">—</option>
                    {subIndustries.map((sub) => (
                        <option key={sub.name} value={sub.name}>{sub.name}</option>
                    ))}
                </select>

                <div className="flex justify-end gap-2">
                    <button
                        onClick={onClose}
                        className="rounded-lg border border-gray-300 px-4 py-2 text-sm hover:bg-gray-100 dark:border-navy-600 dark:hover:bg-navy-700"
                    >
                        Annuler
                    </button>
                    <button
                        onClick={handleSave}
                        disabled={!industry || saving}
                        className="rounded-lg bg-brand-500 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-600 disabled:opacity-50"
                    >
                        {saving ? 'Enregistrement...' : 'Enregistrer'}
                    </button>
                </div>
            </div>
        </div>
    );
}

export default function BackOfficePixels() {
    const [groups, setGroups] = useState<PixelGroup[]>([]);
    const [expandedHostnames, setExpandedHostnames] = useState<Set<string>>(new Set());
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [total, setTotal] = useState(0);

    const [searchInput, setSearchInput] = useState('');
    const [productInput, setProductInput] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [productQuery, setProductQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState<PixelStatus | ''>('');
    const [showArchived, setShowArchived] = useState(false);
    const [availableIndustries, setAvailableIndustries] = useState<IndustryOption[]>([]);

    const [pixelMode, setPixelModeState] = useState<PixelMode | null>(null);
    const [savingPixelMode, setSavingPixelMode] = useState(false);

    const [editingEntry, setEditingEntry] = useState<PixelEntry | null>(null);
    const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
    const [reanalyzing, setReanalyzing] = useState<string | null>(null);
    const [actionError, setActionError] = useState('');

    const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

    const availableProducts = useMemo(() => {
        const names = new Set<string>();
        availableIndustries.forEach((ind) => ind.subIndustries?.forEach((sub) => names.add(sub.name)));
        return Array.from(names).sort((a, b) => a.localeCompare(b));
    }, [availableIndustries]);

    useEffect(() => {
        axios.get(`${CONFIG.apiUrl}/industry`).then((res) => setAvailableIndustries(res.data)).catch(() => {});
    }, []);

    useEffect(() => {
        const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
        axios.get(`${CONFIG.apiUrl}/admin/pixel-config`, { headers }).then((res) => setPixelModeState(res.data.mode)).catch(() => {});
    }, []);

    const updatePixelMode = (mode: PixelMode) => {
        const previous = pixelMode;
        setPixelModeState(mode);
        setSavingPixelMode(true);
        const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
        axios
            .put(`${CONFIG.apiUrl}/admin/pixel-config`, { mode }, { headers })
            .catch(() => setPixelModeState(previous))
            .finally(() => setSavingPixelMode(false));
    };

    // Debounce the two free-text inputs so we don't fire a request on every keystroke.
    useEffect(() => {
        const timeout = setTimeout(() => {
            setSearchQuery(searchInput);
            setProductQuery(productInput);
            setPage(1);
        }, DEBOUNCE_MS);
        return () => clearTimeout(timeout);
    }, [searchInput, productInput]);

    const fetchPixels = () => {
        setLoading(true);
        const params: Record<string, string | number> = { page, limit: 50 };
        if (searchQuery.trim()) params.search = searchQuery.trim();
        if (productQuery.trim()) params.subIndustry = productQuery.trim();
        if (statusFilter) params.status = statusFilter;
        if (showArchived) params.archived = 'true';

        axios
            .get(`${CONFIG.apiUrl}/admin/pixels`, { headers: authHeaders(), params })
            .then((res) => {
                setGroups(res.data.groups);
                setTotalPages(res.data.totalPages);
                setTotal(res.data.total);
            })
            .catch(() => setError('Failed to load pixels.'))
            .finally(() => setLoading(false));
    };

    useEffect(fetchPixels, [page, searchQuery, productQuery, statusFilter, showArchived]);

    const hasFilters = searchInput || productInput || statusFilter;
    const clearFilters = () => {
        setSearchInput('');
        setProductInput('');
        setSearchQuery('');
        setProductQuery('');
        setStatusFilter('');
        setPage(1);
    };

    const toggleGroup = (hostname: string) => {
        setExpandedHostnames((prev) => {
            const next = new Set(prev);
            if (next.has(hostname)) {
                next.delete(hostname);
            } else {
                next.add(hostname);
            }
            return next;
        });
    };

    // Mutations refetch the grouped list rather than patching state locally: an edit/archive/
    // delete on one page can change its group's aggregate totals and industry-mix, which isn't
    // worth recomputing by hand on the client.
    const handleApprove = async (entry: PixelEntry) => {
        setActionError('');
        try {
            await axios.post(`${CONFIG.apiUrl}/admin/pixels/${entry._id}/approve`, {}, { headers: authHeaders() });
            fetchPixels();
        } catch {
            setActionError("Impossible d'approuver ce pixel.");
        }
    };

    const handleReanalyze = async (entry: PixelEntry) => {
        setActionError('');
        setReanalyzing(entry._id);
        try {
            await axios.post(`${CONFIG.apiUrl}/admin/pixels/${entry._id}/reanalyze`, {}, { headers: authHeaders() });
        } catch {
            setActionError('Impossible de relancer la classification de ce pixel.');
        } finally {
            setReanalyzing(null);
        }
    };

    const handleArchive = async (entry: PixelEntry, archived: boolean) => {
        setActionError('');
        try {
            await axios.put(`${CONFIG.apiUrl}/admin/pixels/${entry._id}/archive`, { archived }, { headers: authHeaders() });
            fetchPixels();
        } catch {
            setActionError("Impossible d'archiver ce pixel.");
        }
    };

    const handleDelete = async (id: string) => {
        setActionError('');
        try {
            await axios.delete(`${CONFIG.apiUrl}/admin/pixels/${id}`, { headers: authHeaders() });
            setDeleteConfirm(null);
            fetchPixels();
        } catch {
            setActionError('Impossible de supprimer ce pixel.');
        }
    };

    const handleSaveEdit = async (industry: string, subIndustry: string) => {
        if (!editingEntry) return;
        await axios.put(
            `${CONFIG.apiUrl}/admin/pixels/${editingEntry._id}`,
            { industry, subIndustry },
            { headers: authHeaders() }
        );
        setEditingEntry(null);
        fetchPixels();
    };

    const renderPageRow = (entry: PixelEntry, indented = false) => (
        <tr
            key={entry._id}
            className={`border-b border-gray-100 dark:border-navy-700 ${indented ? 'bg-gray-50/60 dark:bg-navy-900/40' : ''}`}
        >
            <td className="py-3 pr-4">
                <span
                    title={STATUS_LABEL[entry.status]}
                    className={`inline-block h-2.5 w-2.5 rounded-full ${STATUS_DOT_CLASS[entry.status]}`}
                />
            </td>
            <td
                className={`py-3 pr-6 max-w-[320px] truncate font-medium text-navy-700 dark:text-white ${indented ? 'pl-8' : ''}`}
                title={entry.url}
            >
                <a href={entry.url} target="_blank" rel="noreferrer" className="hover:underline">
                    {entry.url}
                </a>
                <div className="truncate text-xs font-normal text-gray-400 dark:text-gray-500" title={entry.sellerEmail}>
                    {entry.sellerEmail || '—'}
                </div>
            </td>
            <td className="py-3 pr-6 text-gray-600 dark:text-gray-400">
                {entry.iframeTemplateName ? (
                    <span
                        className="inline-block max-w-[160px] truncate rounded-full bg-brand-50 px-2 py-0.5 text-xs font-medium text-brand-600 dark:bg-navy-700 dark:text-brand-300"
                        title={entry.iframeTemplateName}
                    >
                        {entry.iframeTemplateName}
                    </span>
                ) : (
                    <span title="Aucun rendu de formulaire universel détecté sur cette URL">—</span>
                )}
            </td>
            <td className="py-3 pr-6 font-semibold text-gray-700 dark:text-gray-300">{entry.last30DaysVisitors}</td>
            <td className="py-3 pr-6 text-gray-600 dark:text-gray-400">
                <span className="inline-flex items-center gap-1">
                    {entry.industry || '—'}
                    {entry.industryManual && (
                        <MdCheckCircle
                            className="text-green-500"
                            size={14}
                            title="Industrie confirmée manuellement — verrouillée pour le classifieur automatique"
                        />
                    )}
                </span>
            </td>
            <td className="py-3 pr-6 text-gray-600 dark:text-gray-400">{entry.subIndustry || '—'}</td>
            <td className="py-3 pr-6 text-gray-500">{formatDate(entry.industryLastAttemptAt)}</td>
            <td className="py-3 pr-6 text-gray-500">{formatRelative(entry.lastSeenAt)}</td>
            <td className="py-3">
                <div className="flex items-center gap-1.5">
                    <button
                        type="button"
                        onClick={() => setEditingEntry(entry)}
                        title="Modifier l'industrie manuellement"
                        className="rounded-lg p-1.5 text-gray-500 hover:bg-gray-100 hover:text-navy-700 dark:hover:bg-navy-700 dark:hover:text-white"
                    >
                        <MdEdit size={16} />
                    </button>
                    {!entry.industryManual && entry.industry && (
                        <button
                            type="button"
                            onClick={() => handleApprove(entry)}
                            title="Approuver l'industrie détectée"
                            className="rounded-lg p-1.5 text-gray-500 hover:bg-gray-100 hover:text-green-600 dark:hover:bg-navy-700"
                        >
                            <MdOutlineCheckCircle size={16} />
                        </button>
                    )}
                    {!entry.industryManual && (
                        <button
                            type="button"
                            onClick={() => handleReanalyze(entry)}
                            disabled={reanalyzing === entry._id}
                            title="Relancer la classification"
                            className="rounded-lg p-1.5 text-gray-500 hover:bg-gray-100 hover:text-navy-700 disabled:opacity-50 dark:hover:bg-navy-700 dark:hover:text-white"
                        >
                            <MdRefresh size={16} className={reanalyzing === entry._id ? 'animate-spin' : ''} />
                        </button>
                    )}
                    <button
                        type="button"
                        onClick={() => handleArchive(entry, !entry.archived)}
                        title={entry.archived ? 'Désarchiver' : 'Archiver'}
                        className="rounded-lg p-1.5 text-gray-500 hover:bg-gray-100 hover:text-navy-700 dark:hover:bg-navy-700 dark:hover:text-white"
                    >
                        {entry.archived ? <MdUnarchive size={16} /> : <MdArchive size={16} />}
                    </button>
                    {deleteConfirm === entry._id ? (
                        <div className="flex gap-1">
                            <button
                                onClick={() => handleDelete(entry._id)}
                                className="rounded bg-red-500 px-2 py-1 text-xs text-white hover:bg-red-600"
                            >
                                Confirmer
                            </button>
                            <button
                                onClick={() => setDeleteConfirm(null)}
                                className="rounded bg-gray-500 px-2 py-1 text-xs text-white hover:bg-gray-600"
                            >
                                Annuler
                            </button>
                        </div>
                    ) : (
                        <button
                            type="button"
                            onClick={() => setDeleteConfirm(entry._id)}
                            title="Supprimer"
                            className="rounded-lg p-1.5 text-gray-500 hover:bg-gray-100 hover:text-red-600 dark:hover:bg-navy-700"
                        >
                            <MdDelete size={16} />
                        </button>
                    )}
                </div>
            </td>
        </tr>
    );

    // A group with a single page is just that page — no point showing an expandable "site"
    // wrapper around exactly one row. Multi-page groups get a summary header (aggregate visits,
    // mixed-industry indicator) that expands to the same per-page rows/actions as a flat list.
    const renderGroupRows = (group: PixelGroup) => {
        if (group.pageCount === 1) {
            return [renderPageRow(group.pages[0])];
        }

        const isExpanded = expandedHostnames.has(group.hostname);
        const rows = [
            <tr
                key={`group-${group.hostname}`}
                onClick={() => toggleGroup(group.hostname)}
                className="cursor-pointer border-b border-gray-100 bg-gray-50 hover:bg-gray-100 dark:border-navy-700 dark:bg-navy-900/60 dark:hover:bg-navy-900"
            >
                <td className="py-3 pr-4">
                    <span
                        title={STATUS_LABEL[group.status]}
                        className={`inline-block h-2.5 w-2.5 rounded-full ${STATUS_DOT_CLASS[group.status]}`}
                    />
                </td>
                <td className="py-3 pr-6 font-medium text-navy-700 dark:text-white">
                    <div className="flex items-center gap-1">
                        {isExpanded ? <MdExpandMore size={18} /> : <MdChevronRight size={18} />}
                        <a
                            href={group.rootUrl}
                            target="_blank"
                            rel="noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="hover:underline"
                        >
                            {group.rootUrl}
                        </a>
                        <span className="ml-1 rounded-full bg-gray-200 px-2 py-0.5 text-xs font-semibold text-gray-600 dark:bg-navy-700 dark:text-gray-300">
                            {group.pageCount} pages
                        </span>
                    </div>
                    <div className="truncate pl-[26px] text-xs font-normal text-gray-400 dark:text-gray-500" title={group.sellerEmail}>
                        {group.sellerEmail || '—'}
                    </div>
                </td>
                <td className="py-3 pr-6 text-gray-600 dark:text-gray-400">
                    {group.iframeMixed ? 'Mixte' : group.iframeTemplateName || '—'}
                </td>
                <td className="py-3 pr-6 font-semibold text-gray-700 dark:text-gray-300">{group.totalVisitors}</td>
                <td className="py-3 pr-6 text-gray-600 dark:text-gray-400">{group.industryMixed ? 'Mixte' : group.industry || '—'}</td>
                <td className="py-3 pr-6 text-gray-600 dark:text-gray-400">{group.industryMixed ? '—' : group.subIndustry || '—'}</td>
                <td className="py-3 pr-6 text-gray-500">—</td>
                <td className="py-3 pr-6 text-gray-500">{formatRelative(group.lastSeenAt)}</td>
                <td className="py-3" />
            </tr>,
        ];
        if (isExpanded) {
            rows.push(...group.pages.map((entry) => renderPageRow(entry, true)));
        }
        return rows;
    };

    return (
        <div className="pt-[50px]">
            <Card extra="p-5">
                <div className="mb-6 flex items-center gap-3">
                    <MdSensors className="text-2xl text-brand-500" />
                    <h2 className="text-2xl font-bold text-navy-700 dark:text-white">Pixels</h2>
                    <span className="ml-2 rounded-full bg-gray-100 px-3 py-0.5 text-sm font-semibold text-gray-700 dark:bg-navy-700 dark:text-gray-300">
                        {total} sites
                    </span>
                </div>

                <div className="mb-6 rounded-xl border border-gray-200 p-4 dark:border-navy-600">
                    <div className="mb-3 flex items-center gap-2">
                        <MdSettings className="text-lg text-gray-500 dark:text-gray-400" />
                        <h3 className="font-semibold text-navy-700 dark:text-white">Installation du pixel</h3>
                        {savingPixelMode && <span className="text-xs text-gray-400">Enregistrement...</span>}
                    </div>
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                        {PIXEL_MODE_OPTIONS.map((opt) => (
                            <button
                                key={opt.value}
                                type="button"
                                onClick={() => updatePixelMode(opt.value)}
                                className={`rounded-lg border p-3 text-left transition-colors ${
                                    pixelMode === opt.value
                                        ? 'border-brand-500 bg-brand-50 dark:bg-navy-700'
                                        : 'border-gray-200 hover:border-brand-400 dark:border-navy-600 dark:hover:border-brand-400'
                                }`}
                            >
                                <div className="text-sm font-semibold text-navy-700 dark:text-white">{opt.label}</div>
                                <div className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">{opt.desc}</div>
                            </button>
                        ))}
                    </div>
                </div>

                <div className="mb-4 flex flex-wrap items-end gap-3">
                    <div className="flex-1 min-w-[220px]">
                        <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">Rechercher (site, industrie, produit)</label>
                        <input
                            type="text"
                            value={searchInput}
                            onChange={(e) => setSearchInput(e.target.value)}
                            placeholder="ex: ecorenov.fr, Assurance..."
                            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none dark:border-navy-600 dark:bg-navy-700 dark:text-white dark:placeholder-gray-400"
                        />
                    </div>

                    <div className="w-[220px]">
                        <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">Produit</label>
                        <ProductSearchInput value={productInput} onChange={setProductInput} options={availableProducts} />
                    </div>

                    <div>
                        <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">Statut</label>
                        <select
                            value={statusFilter}
                            onChange={(e) => { setStatusFilter(e.target.value as PixelStatus | ''); setPage(1); }}
                            className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                        >
                            <option value="">Tous</option>
                            {STATUS_FILTER_OPTIONS.map((opt) => (
                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                        </select>
                    </div>

                    <button
                        type="button"
                        onClick={() => { setShowArchived((prev) => !prev); setPage(1); }}
                        className={`flex items-center gap-1 rounded-lg border px-3 py-2 text-xs ${
                            showArchived
                                ? 'border-brand-500 bg-brand-50 text-brand-600 dark:bg-navy-700'
                                : 'border-gray-300 hover:bg-gray-100 dark:border-navy-600 dark:hover:bg-navy-700'
                        }`}
                    >
                        <MdArchive size={14} /> {showArchived ? 'Pixels archivés' : 'Voir les archivés'}
                    </button>

                    {hasFilters && (
                        <button
                            type="button"
                            onClick={clearFilters}
                            className="flex items-center gap-1 rounded-lg border border-gray-300 px-3 py-2 text-xs hover:bg-gray-100 dark:border-navy-600 dark:hover:bg-navy-700"
                        >
                            <MdClose size={14} /> Effacer filtres
                        </button>
                    )}
                </div>

                {error && (
                    <div className="mb-4 rounded-lg bg-red-100 p-3 text-sm text-red-700 dark:bg-red-900/30 dark:text-red-400">{error}</div>
                )}
                {actionError && (
                    <div className="mb-4 rounded-lg bg-red-100 p-3 text-sm text-red-700 dark:bg-red-900/30 dark:text-red-400">{actionError}</div>
                )}

                {loading ? (
                    <div className="py-10 text-center text-gray-400">Loading...</div>
                ) : groups.length === 0 ? (
                    <div className="py-10 text-center text-gray-400">No pixel hits recorded yet.</div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-left text-sm">
                            <thead>
                                <tr className="border-b border-gray-200 dark:border-navy-600">
                                    <th className="pb-3 pr-4 text-xs font-bold uppercase text-gray-500">Status</th>
                                    <th className="pb-3 pr-6 text-xs font-bold uppercase text-gray-500">Website</th>
                                    <th className="pb-3 pr-6 text-xs font-bold uppercase text-gray-500">Iframe lié</th>
                                    <th className="pb-3 pr-6 text-xs font-bold uppercase text-gray-500">Visitors (30d)</th>
                                    <th className="pb-3 pr-6 text-xs font-bold uppercase text-gray-500">Industrie détectée</th>
                                    <th className="pb-3 pr-6 text-xs font-bold uppercase text-gray-500">Produit détecté</th>
                                    <th className="pb-3 pr-6 text-xs font-bold uppercase text-gray-500">Dernière tentative de classification</th>
                                    <th className="pb-3 pr-6 text-xs font-bold uppercase text-gray-500">Dernière visite</th>
                                    <th className="pb-3 text-xs font-bold uppercase text-gray-500">Actions</th>
                                </tr>
                            </thead>
                            <tbody>{groups.flatMap((group) => renderGroupRows(group))}</tbody>
                        </table>
                    </div>
                )}

                {totalPages > 1 && (
                    <div className="mt-5 flex items-center justify-between">
                        <div className="text-sm text-gray-600 dark:text-gray-400">
                            Page {page} of {totalPages}
                        </div>
                        <div className="flex gap-2">
                            <button
                                onClick={() => setPage(page - 1)}
                                disabled={page === 1}
                                className="flex items-center gap-1 rounded-lg border border-gray-300 px-3 py-2 text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 dark:border-navy-600 dark:hover:bg-navy-700"
                            >
                                <MdChevronLeft />
                                Previous
                            </button>
                            <button
                                onClick={() => setPage(page + 1)}
                                disabled={page === totalPages}
                                className="flex items-center gap-1 rounded-lg border border-gray-300 px-3 py-2 text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 dark:border-navy-600 dark:hover:bg-navy-700"
                            >
                                Next
                                <MdChevronRight />
                            </button>
                        </div>
                    </div>
                )}
            </Card>

            {editingEntry && (
                <EditPixelModal
                    entry={editingEntry}
                    industries={availableIndustries}
                    onClose={() => setEditingEntry(null)}
                    onSave={handleSaveEdit}
                />
            )}
        </div>
    );
}
